/**
 * NativeProxyManager - Advanced proxy creation with caching and validation
 */

import { CallOptions } from "../types/protocol";
import { BifrostWeb } from "../BifrostWeb";
import { MethodConfiguration } from "./ProxyBuilder";

export interface ProxyOptions {
  enableMethodChaining?: boolean;
  validateCalls?: boolean;
  cacheResults?: boolean;
  defaultCallOptions?: CallOptions; // Default call options for all methods on this proxy (overrides global, can be overridden per call)
  methodOptions?: MethodConfiguration; // Method-specific call options (overrides proxy defaults, can be overridden per call)
}

export interface ProxyMetadata {
  serviceName: string;
  createdAt: Date;
  callCount: number;
  options: ProxyOptions;
  defaultCallOptions?: CallOptions; // Proxy-level default call options
  methodOptions?: MethodConfiguration; // Method-specific call options
}

/**
 * Enhanced native proxy manager with caching, validation, and TypeScript type safety
 */
export class NativeProxyManager {
  private proxyCache = new Map<string, any>();
  private proxyMetadata = new Map<string, ProxyMetadata>();
  private isDestroyed = false;

  constructor(private bifrostWeb: BifrostWeb) {}

  /**
   * Create a typed native proxy with caching and validation
   */
  createProxy<T = any>(serviceName: string, options: ProxyOptions = {}): T {
    this.throwIfDestroyed();
    this.validateServiceName(serviceName);

    // Return cached proxy if it exists
    if (this.proxyCache.has(serviceName)) {
      return this.proxyCache.get(serviceName) as T;
    }

    // Create new proxy with enhanced features
    const proxy = this.createEnhancedProxy<T>(serviceName, options);

    // Cache the proxy
    this.proxyCache.set(serviceName, proxy);

    // Store metadata
    this.proxyMetadata.set(serviceName, {
      serviceName,
      createdAt: new Date(),
      callCount: 0,
      options: { ...options },
      defaultCallOptions: options.defaultCallOptions,
      methodOptions: options.methodOptions,
    });

    return proxy;
  }

  /**
   * Get metadata for a cached proxy
   */
  getProxyMetadata(serviceName: string): ProxyMetadata | undefined {
    return this.proxyMetadata.get(serviceName);
  }

  /**
   * List all cached proxy service names
   */
  listCachedProxies(): string[] {
    return Array.from(this.proxyCache.keys());
  }

  /**
   * Clear specific proxy from cache
   */
  clearProxy(serviceName: string): void {
    this.proxyCache.delete(serviceName);
    this.proxyMetadata.delete(serviceName);
  }

  /**
   * Clear all proxies from cache
   */
  clearAllProxies(): void {
    this.proxyCache.clear();
    this.proxyMetadata.clear();
  }

  /**
   * Destroy the proxy manager and cleanup resources
   */
  destroy(): void {
    this.clearAllProxies();
    this.isDestroyed = true;
  }

  /**
   * Create enhanced proxy with advanced features
   */
  private createEnhancedProxy<T>(
    serviceName: string,
    options: ProxyOptions,
  ): T {
    const handler: ProxyHandler<any> = {
      get: (_target, prop: string | symbol) => {
        if (typeof prop !== "string") {
          return undefined;
        }

        return (...args: any[]) => {
          // Get metadata and increment call count
          const metadata = this.proxyMetadata.get(serviceName);
          if (metadata) {
            metadata.callCount++;
          }

          // Extract options from last parameter if present
          let callOptions: CallOptions | undefined;
          let methodArgs = args;

          // Check if last argument looks like options
          if (
            args.length > 0 &&
            this.isOptionsParameter(args[args.length - 1])
          ) {
            callOptions = args[args.length - 1];
            methodArgs = args.slice(0, -1);
          }

          // Determine payload - single arg becomes the payload, multiple args become array
          let payload: any;
          if (methodArgs.length === 0) {
            payload = undefined;
          } else if (methodArgs.length === 1) {
            payload = methodArgs[0];
          } else {
            payload = methodArgs;
          }

          // Validate calls if enabled
          if (options.validateCalls) {
            this.validateMethodCall(serviceName, prop, payload);
          }

          // Get proxy-level default options from metadata
          const proxyCallOptions = metadata?.defaultCallOptions;

          // Get method-specific options from metadata
          const methodCallOptions = metadata?.methodOptions?.[prop];

          // Merge options: global → proxy → method → call
          // BifrostWeb.mergeCallOptions handles global + proxy + method + call merging
          const mergedOptions = this.bifrostWeb.mergeCallOptions(
            proxyCallOptions,
            methodCallOptions,
            callOptions,
          );

          // Determine if this is a notification (void method) or call (promise method)
          // For now, we'll treat all methods as calls unless specifically configured
          // In a real implementation, this could be inferred from TypeScript types
          if (this.isNotificationMethod(prop, options)) {
            this.bifrostWeb.notify(serviceName, prop, payload);
            return; // void return for notifications
          } else {
            // Return promise for calls with merged options
            return this.bifrostWeb.call(
              serviceName,
              prop,
              payload,
              mergedOptions,
            );
          }
        };
      },
    };

    // Create proxy with empty target object
    return new Proxy({}, handler) as T;
  }

  /**
   * Validate service name format
   */
  private validateServiceName(serviceName: string): void {
    if (!serviceName || typeof serviceName !== "string") {
      throw new Error(`Invalid service name: ${serviceName}`);
    }

    // Service name must start with letter and contain only letters, numbers, underscore
    const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
    if (!validNamePattern.test(serviceName)) {
      throw new Error(`Invalid service name: ${serviceName}`);
    }
  }

  /**
   * Check if parameter looks like call options
   */
  private isOptionsParameter(param: any): param is CallOptions {
    return (
      param &&
      typeof param === "object" &&
      !Array.isArray(param) &&
      (param.timeout !== undefined ||
        param.retryConfig !== undefined ||
        param.binary !== undefined)
    );
  }

  /**
   * Validate method call (placeholder for enhanced validation)
   */
  private validateMethodCall(
    serviceName: string,
    method: string,
    _payload: any,
  ): void {
    // Basic validation - could be enhanced with schema validation
    if (!method || typeof method !== "string") {
      throw new Error(
        `Invalid method name for service ${serviceName}: ${method}`,
      );
    }

    // Additional validations could include:
    // - Parameter type checking
    // - Schema validation
    // - Service method existence checking
  }

  /**
   * Determine if method should be treated as notification
   * This is a placeholder - in practice could use TypeScript reflection or configuration
   */
  private isNotificationMethod(
    method: string,
    _options: ProxyOptions,
  ): boolean {
    // Simple heuristic: methods named 'notify' or ending in 'Notify' are notifications
    return (
      method === "notify" ||
      method.endsWith("Notify") ||
      method.startsWith("notify")
    );
  }

  /**
   * Throw error if proxy manager has been destroyed
   */
  private throwIfDestroyed(): void {
    if (this.isDestroyed) {
      throw new Error("NativeProxyManager has been destroyed");
    }
  }
}
